// NPM
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Alert,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  Stack,
  TextField,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

// COMPONENTS
import CustomModal from '../CustomModal';
import HelpTooltip from '../HelpTooltip';

// I18N
import CustomersFormattedMessages from '../../../messages/admin/CustomersFormattedMessages';

// NETWORK
import useCustomerRequest from '../../../requests/CustomerRequest';
import Loader from '../Loader';

const ManagePoolNumber = ({ errorHandler, customer, callBack }) => {
  const [error, setError] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [available, setAvailable] = useState([]); // list of numbers that are available
  const [desired, setDesired] = useState([]); // list of numbers to be added
  const [poolNumbers, setPoolNumbers] = useState(null); // pool numbers
  const [undesired, setUndesired] = useState({ numbers: [] }); // list of numbers to be removed
  const [edits, setEdits] = useState({});
  const [campaignEdits, setCampaignEdits] = useState({});
  const [isSearching, setIsSearching] = useState(false); // should toggle while waiting for twilio response.
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [isChangingCampaign, setIsChangingCampaign] = useState(false);
  const [selected, setSelected] = useState(0); // number of numbers selected for provisioning?
  const [campaigns, setCampaigns] = useState(null);
  const [campaignCode, setCampaignCode] = useState(undefined); // stores value of campaign dropdown when provisioning.
  const [templateDeviceTn, setTemplateDeviceTn] = useState('+12049008550'); //Twilio template tn
  const [legacyReleaseCheck, setLegacyReleaseCheck] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState('Twilio');
  const [selectedTemplateDevice, setSelectedTemplateDevice] = useState('6602');
  const [templateDeviceValue, setTemplateDeviceValue] = useState('');
  const [showReleaseButton, setShowReleaseButton] = useState(false);
  const [showProvisionButton, setShowProvisionButton] = useState(false);
  const {
    getPoolNumbers,
    getCustomerCampaignData,
    getTwilioProvisional,
    changeTwilioNumbers,
    provisionNumbers,
    releaseTwilioNumbers,
  } = useCustomerRequest();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const poolNumbers = await getPoolNumbers(
          customer.customer_id,
          errorHandler
        );
        console.log('pool numbers:', poolNumbers);
        const toBeReleased = { numbers: [] };

        const campaigns = await getCustomerCampaignData(
          customer.customer_id,
          errorHandler
        );
        console.log(`campaigns:${JSON.stringify(campaigns, null, 2)}`);

        setAvailable([]);
        setDesired([]);
        setUndesired(toBeReleased);
        setPoolNumbers(poolNumbers);
        setCampaigns(campaigns.currentCampaigns);
      } catch (error) {
        errorHandler(error);
      }
    };

    fetchData();
  }, [customer.customer_id, errorHandler]);

  const generateErrorText = () => {
    if (!hasError) return null;

    return <Alert severity="error">{error}</Alert>;
  };

  const handleChange = (e) => {
    const target = e.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    if (target.type === 'checkbox') {
      console.log('Checked a checkbox');
      console.log('target:', target);

      if (target.name === 'provision') {
        if (target.checked) {
          desired.push(target.value);
        } else {
          const pos = desired.indexOf(target.value);
          desired.splice(pos, 1);
        }
        console.log('desired:', desired);
      }

      if (target.name === 'release') {
        const udItem = {};
        udItem.device_id = target.id;
        udItem.phoneNumber = target.value;

        if (target.checked) {
          undesired.numbers.push(udItem);
        } else {
          const pos = undesired.numbers.indexOf(udItem);
          undesired.numbers.splice(pos, 1);
        }
        console.log('undesired', undesired);
      }
    }

    if (name === 'campaignCode') {
      setCampaignCode(value);
      return;
    }

    if (name === 'template_device_id') {
      setTemplateDeviceTn(value);
      return;
    }

    if (name === 'campaignChange') {
      const index = poolNumbers.findIndex((e) => {
        if (e.device_id.toString() === target.id) {
          if (value === 'N/A' && e.campaignCode !== undefined) {
            return true; // user selects no campaign when there was one selected.
          } else if (value !== 'N/A' && value !== e.campaignCode) {
            return true; // user selects a code where previously there was none
          }
        }
      });
      console.log('index: ', index);
      if (index !== -1) campaignEdits[target.id] = value;
      // If its the same campaign, then delete from the edits object.
      else delete campaignEdits[target.id];

      console.log(`campaignEdits:${JSON.stringify(campaignEdits, null, 2)}`);

      setCampaignEdits(campaignEdits);
      return;
    }

    const updatedEdits = { ...edits, [name]: value };
    console.log('Edits:', updatedEdits);

    setEdits(updatedEdits);
    setDesired(desired);
    setUndesired(undesired);
    setShowReleaseButton(undesired.numbers.length > 0);
    setShowProvisionButton(desired.length > 0);
    setHasError(false);
    setError('');
  };

  const validateForm = () => {
    let isValidForm = false;
    let errText = '';

    console.log('edits.prefix: ', edits.prefix);
    // prefix must be 3 digit number
    if (edits.prefix && edits.prefix.length === 3 && !isNaN(edits.prefix)) {
      isValidForm = true;

      // number filter must be more than one character
    } else if (edits.numberContains && edits.numberContains.length > 1) {
      isValidForm = true;
    } else {
      errText = 'Requires a 3-digit prefix or number filter thats 2+ digits.';
    }
    // If no error text, we have a valid form
    if (!errText) isValidForm = true;

    setHasError(!isValidForm);
    setError(errText);

    return {
      isValidForm: isValidForm,
      formError: errText,
    };
  };

  const handleSearch = async () => {
    const result = validateForm();
    console.log(result);
    if (!result.isValidForm) {
      generateErrorText();

      console.log('form Validation Failed');
      return;
    }

    const data = {
      prefix: edits.prefix,
      country: edits.country || 'US',
      selectedGateway,
      numberContains: edits.numberContains,
    };

    setIsSearching(true);

    try {
      const newNumbersObject = await getTwilioProvisional(data, errorHandler);

      if (newNumbersObject === 'undefined' || newNumbersObject.error) {
        console.log('Error retrieving numbers from Twilio');
        // this.props.callBack(true);
      } else {
        setAvailable(newNumbersObject);
        setDesired([]);
      }
    } catch (error) {
      errorHandler(error);
    }

    clearCheckboxes('provision');
    setIsSearching(false);
  };

  const clearCheckboxes = (name) => {
    const elements = document.getElementsByClassName(
      'form-check-input position-static'
    );
    for (let i = 0, len = elements.length; i < len; i++) {
      if ((elements[i] ? elements[i].name : null) === name)
        elements[i].checked = false;
    }
  };

  const changeCampaignForNumber = async () => {
    setIsChangingCampaign(true);
    try {
      const response = await changeTwilioNumbers(
        campaignEdits,
        customer.customer_id,
        errorHandler
      );
      console.log(`response:${JSON.stringify(response, null, 2)}`);

      const poolNumbers = await getPoolNumbers(
        customer.customer_id,
        errorHandler
      );
      console.log('Pool Numbers after provision:');
      console.log(`poolNumbers:${JSON.stringify(poolNumbers, null, 2)}`);

      setCampaignEdits({});
      setPoolNumbers(poolNumbers);
    } catch (error) {
      errorHandler(error);
    }

    setIsChangingCampaign(false);
  };

  const handleProvision = async () => {
    console.log('handleProvision called');
    setIsProvisioning(true);

    const data = {
      templateDeviceId:
        selectedTemplateDevice || getTemplateDeviceOptions(selectedGateway)?.id,
      numbers: desired,
      customerId: customer.customer_id,
      campaignCode: campaignCode,
      gateway: selectedGateway,
    };
    console.log('data: ', data);

    try {
      let response = await provisionNumbers(data, errorHandler);
      console.log('Provision response:', response);

      response = await getPoolNumbers(customer.customer_id, errorHandler);
      console.log('Pool Numbers after provision:', response);

      clearCheckboxes('provision');
      setPoolNumbers(response);
      setAvailable([]);
      setDesired([]);
      setCampaignCode(undefined);
    } catch (error) {
      errorHandler(error);
    }

    setIsProvisioning(false);
  };

  const handleRelease = async (checkFlag) => {
    console.log('handleRelease called');
    setIsReleasing(true);
    setLegacyReleaseCheck(false);

    console.log('undesired:', undesired);

    if (checkFlag) {
      for (let i = 0; i < undesired.numbers.length; i++) {
        const undesiredNumber = undesired.numbers[i];
        // To get the device info of the number to be released.
        const deviceIndex = poolNumbers.findIndex((element) => {
          return element.device_id === undesiredNumber.device_id;
        });

        // If it didn't find the pool number with the selected device id, there has been an error somewhere.
        if (deviceIndex !== -1) {
          const legacyCheck =
            poolNumbers[deviceIndex].device_name?.indexOf('AWS');
          if (legacyCheck === -1 || legacyCheck === undefined) {
            setLegacyReleaseCheck(true);
            return;
          }
        }
      }
    }

    console.log('data:', undesired);

    try {
      await releaseTwilioNumbers(undesired, errorHandler);

      const response = await getPoolNumbers(customer.customer_id, errorHandler);
      console.log('Pool Numbers after release:', response);

      const ud = { numbers: [] };

      clearCheckboxes('release');
      setPoolNumbers(response);
      setUndesired(ud);
    } catch (error) {
      errorHandler(error);
    }

    setIsReleasing(false);
  };

  const toggleSearching = () => {
    setIsSearching(!isSearching);
  };

  const toggleProvisioning = () => {
    setIsProvisioning(!isProvisioning);
  };

  const toggleReleasing = (target) => {
    setIsReleasing(target || !isReleasing);
  };

  const generateAvailableTable = () => {
    const htmlRows = [];

    console.log('generateAvailableTable:', available);

    if (available === undefined || !available || available.length === 0) {
      return;
    }

    available.forEach((item, i) => {
      htmlRows.push(
        <TableRow key={i}>
          <TableCell>{item.phoneNumber}</TableCell>
          <TableCell>{item.locality}</TableCell>
          <TableCell>
            <Checkbox
              type="checkbox"
              value={item.phoneNumber}
              name="provision"
              size="small"
            />
          </TableCell>
        </TableRow>
      );
    });

    return (
      <Box>
        {generateCampaignDropdown()}

        <Box sx={{ display: 'flex', justifyContent: 'end', marginBottom: 1 }}>
          {generateProvisionButton()}
        </Box>
        <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ whiteSpace: 'nowrap' }}>
              <TableRow>
                <TableCell>Phone Number</TableCell>
                <TableCell>Locality</TableCell>
                <TableCell>Provision?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{htmlRows}</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const generatePoolNumbersTable = () => {
    // Add any routes that supports changing campaigns to this var.
    const changeableRoutes = ['Twilio'];

    if (!poolNumbers) return <Loader />;

    if (poolNumbers.length === 0)
      return (
        <Typography gutterBottom>
          {CustomersFormattedMessages.noPoolNumbers}
        </Typography>
      );

    // Need to show different options depending on the route because we can't change the campaign from
    // Twilio to Telnyx for example.
    const differentOptions = {
      default: [
        <MenuItem key={'N/A'} value={'N/A'}>
          {'N/A'}
        </MenuItem>,
      ],
    };

    campaigns.forEach(({ campaign_code, campaign_type }, i) => {
      if (!differentOptions[campaign_type]) {
        differentOptions[campaign_type] = [
          <MenuItem key={'N/A'} value={'N/A'}>
            {'N/A'}
          </MenuItem>,
        ];
      }

      differentOptions[campaign_type].push(
        <MenuItem key={i} value={campaign_code}>
          {campaign_code}
        </MenuItem>
      );
    });

    const htmlRows = poolNumbers.map(
      ({ device_name, device_id, route_name, campaignCode, reply_tn }, i) => {
        // So far, the first word of the virtual device is the route type e.g. Twilio
        const splitArray = route_name?.split(' ');
        const routeType = splitArray?.length ? splitArray[0] : 'default';

        // Not every route type supports changing the campaign for numbers
        const isChangeable = changeableRoutes.includes(routeType);

        // Creates dropdown select for the campaign if current campaign is valid for the new system.
        let campaignContent = (
          <Select
            onChange={handleChange}
            defaultValue={campaignCode || 'N/A'}
            name="campaignChange"
            size="small"
            fullWidth
            disabled={!isChangeable}
          >
            {differentOptions[routeType]}
          </Select>
        );

        // If campaign wasn't found for the customer in the new system, its probably legacy or broken.
        if (
          !campaigns ||
          campaigns.findIndex((e) => {
            if (e.campaign_code === campaignCode || campaignCode === undefined)
              return true;
          }) === -1
        ) {
          campaignContent = campaignCode || 'N/A';
        }

        // Pushes rows for table.
        return (
          <TableRow key={i}>
            <TableCell>{device_name}</TableCell>
            <TableCell>{campaignContent}</TableCell>
            <TableCell>{route_name}</TableCell>
            <TableCell>{reply_tn}</TableCell>
            <TableCell>
              <Checkbox
                type="checkbox"
                id={device_id}
                value={reply_tn}
                name="release"
                size="small"
              />
            </TableCell>
          </TableRow>
        );
      }
    );

    const {
      campaign,
      deviceName,
      release,
      gateway,
      poolNumber,
      saveChanges,
      saving,
    } = CustomersFormattedMessages;

    let button = null;
    if (isChangingCampaign) {
      button = <p>{saving}</p>;
    } else if (campaignEdits && Object.keys(campaignEdits).length !== 0) {
      button = (
        <Button
          type="button"
          onClick={changeCampaignForNumber}
          color="secondary"
          variant="contained"
        >
          {saveChanges}
        </Button>
      );
    }

    return (
      <Box>
        <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ whiteSpace: 'nowrap' }}>
              <TableRow>
                <TableCell>{deviceName}</TableCell>
                <TableCell>{campaign}</TableCell>
                <TableCell>{gateway}</TableCell>
                <TableCell>{poolNumber}</TableCell>
                <TableCell>{release}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{htmlRows}</TableBody>
          </Table>
        </TableContainer>
        {button}
      </Box>
    );
  };

  const generateLander = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
          marginBottom: 2,
        }}
      >
        {generateReleaseButton()}
      </Box>
    );
  };

  const generateSearchButton = () => {
    // If in Search State
    if (isSearching) return <p>Searching...</p>;

    return (
      <Button
        type="button"
        onClick={handleSearch}
        color="secondary"
        variant="outlined"
      >
        Search for available numbers
      </Button>
    );
  };

  const generateProvisionButton = () => {
    return (
      <>
        {isProvisioning ? (
          <Typography sx={{ textAlign: 'right' }}>Provisioning...</Typography>
        ) : showProvisionButton ? (
          <Button
            type="button"
            onClick={handleProvision}
            color="secondary"
            variant="contained"
          >
            Provision selected
          </Button>
        ) : null}
      </>
    );
  };

  const generateReleaseButton = () => {
    return (
      <>
        {isReleasing ? (
          <Typography sx={{ textAlign: 'right' }}>Releasing...</Typography>
        ) : showReleaseButton ? (
          <Button
            type="button"
            onClick={() => handleRelease(true)}
            color="secondary"
            variant="contained"
          >
            Release selected
          </Button>
        ) : null}
      </>
    );
  };

  const generateCampaignDropdown = () => {
    if (!campaigns) return null;

    const options = [
      <MenuItem key={'None'} value={''}>
        None
      </MenuItem>,
    ];

    for (let i = 0; i < campaigns.length; i++) {
      if (selectedGateway === campaigns[i].campaign_type) {
        options.push(
          <MenuItem key={i} value={campaigns[i].campaign_code}>
            {campaigns[i].campaign_code}
          </MenuItem>
        );
      }
    }

    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        maxWidth={800}
        marginTop={4}
        marginBottom={1}
      >
        <InputLabel sx={{ minWidth: 200 }}>
          Provision against campaign
        </InputLabel>
        <Select
          defaultValue=""
          onChange={handleChange}
          name="campaignCode"
          size="small"
          fullWidth
        >
          {options}
        </Select>
      </Stack>
    );
  };

  const generateWarningModal = () => {
    return (
      <CustomModal
        open={legacyReleaseCheck}
        onClose={() => {
          setLegacyReleaseCheck(false);
          setIsReleasing(false);
        }}
        title={CustomersFormattedMessages.modalWarning}
      >
        <Typography gutterBottom>
          {CustomersFormattedMessages.modalBody}
        </Typography>

        <Box display="flex" gap={2}>
          <Button
            color="secondary"
            variant="outlined"
            onClick={() => {
              setLegacyReleaseCheck(false);
              setIsReleasing(false);
            }}
          >
            {CustomersFormattedMessages.modalCancel}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => handleRelease(false)}
          >
            {CustomersFormattedMessages.modalYes}
          </Button>
        </Box>
      </CustomModal>
    );
  };

  const handleGatewayDropdown = (event) => {
    const selectedValue = event.target.value;
    const selectedTemplateObject =
      getTemplateDeviceOptions(selectedValue) || {};

    // Switches the country if gateway switched to telnyx since telnyx only supports US for now
    if (selectedValue === 'Telnyx') edits.country = 'US';

    const stateChange = {
      selectedGateway: selectedValue,
      templateDeviceValue: '',
      available: [],
      desired: [],
      edits: edits,
    };

    setSelectedGateway(stateChange.selectedGateway);
    setSelectedTemplateDevice(selectedTemplateObject?.id);
    setTemplateDeviceValue(stateChange.templateDeviceValue);
    setAvailable(stateChange.available);
    setDesired(stateChange.desired);
    setEdits(stateChange.edits);

    clearCheckboxes('provision');
  };

  const handleTemplateDeviceDropdown = (event) => {
    const selectedValue = event.target.value;

    setSelectedTemplateDevice(selectedValue);
    setTemplateDeviceValue('');
  };

  const getTemplateDeviceOptions = (selectedValue) => {
    if (selectedValue === 'Twilio')
      return {
        name: 'Twilio Canada #6602 Group (UKG Pool)',
        id: '6602',
      };
    else if (selectedValue === 'Telnyx')
      return {
        name: 'Telnyx #7002 Group (Template)',
        id: '7002',
      };
    else if (selectedValue === 'Bandwidth') {
      if (process.env.REACT_APP_ENV === 'production') {
        return {
          name: 'Bandwidth #8004 Group (Prod Template)',
          id: '8004',
        };
      } else {
        return {
          name: 'Bandwidth #8003 Group (Dev Template)',
          id: '8003',
        };
      }
    }
  };

  const generateDropdowns = () => {
    const options = [];

    if (selectedGateway === 'Twilio') {
      options.push(
        <MenuItem key={'6602'} value={'6602'}>
          {'Twilio Canada #6602 Group (UKG Pool)'}
        </MenuItem>
      );
    } else if (selectedGateway === 'Telnyx') {
      options.push(
        <MenuItem key={'7002'} value={'7002'}>
          {'Telnyx #7002 Group (Template)'}
        </MenuItem>
      );
    } else if (selectedGateway === 'Bandwidth') {
      options.push(
        <MenuItem key={'8003'} value={'8003'}>
          {'Bandwidth #8003 Group (Dev Template)'}
        </MenuItem>
      );
      options.push(
        <MenuItem key={'8004'} value={'8004'}>
          {'Bandwidth #8004 Group (Prod Template)'}
        </MenuItem>
      );
    }

    return (
      <>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ md: 'center' }}
        >
          <InputLabel sx={{ minWidth: 160 }}>Gateway</InputLabel>

          <Select
            value={selectedGateway}
            onChange={handleGatewayDropdown}
            size="small"
            fullWidth
          >
            <MenuItem value="Twilio">Twilio</MenuItem>
            <MenuItem value="Telnyx">Telnyx</MenuItem>
            <MenuItem value="Bandwidth">Bandwidth</MenuItem>
          </Select>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ md: 'center' }}
        >
          <InputLabel sx={{ minWidth: 160 }}>Template Device</InputLabel>
          <Select
            value={selectedTemplateDevice}
            onChange={handleTemplateDeviceDropdown}
            name="template_device_id"
            size="small"
            fullWidth
          >
            {options}
          </Select>
        </Stack>
      </>
    );
  };

  const generateTextFormGroup = (
    label,
    controlId,
    isRequired = false,
    setType
  ) => {
    let type = 'text';
    if (setType) type = setType;
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
      >
        <InputLabel sx={{ minWidth: 160 }} required={isRequired}>
          {label}
        </InputLabel>

        <TextField
          fullWidth
          id={controlId}
          name={controlId}
          onChange={handleChange}
          size="small"
          required={isRequired}
        />
      </Stack>
    );
  };

  const generateNumberContainsFormGroup = () => {
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
      >
        <InputLabel sx={{ minWidth: 160, display: 'flex' }} required>
          {CustomersFormattedMessages.numberContains}
          <HelpTooltip
            message={
              <Stack>
                <Typography>
                  {CustomersFormattedMessages.numberContainsTooltip}
                </Typography>
              </Stack>
            }
          />
        </InputLabel>

        <TextField
          required
          fullWidth
          name="numberContains"
          value={edits.numberContains || ''}
          onChange={handleChange}
          size="small"
          error={hasError}
          helperText={error}
        />
      </Stack>
    );
  };

  const generateCountryPicker = () => {
    const includesCanada = selectedGateway !== 'Telnyx';

    // Push options for country dropdown
    const options = [
      <MenuItem key={'US'} value={'US'}>
        United States
      </MenuItem>,
    ];

    if (includesCanada) {
      options.push(
        <MenuItem key={'CA'} value={'CA'}>
          Canada
        </MenuItem>
      );
    }

    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
      >
        <InputLabel sx={{ minWidth: 160 }}>Country</InputLabel>
        <Select
          value={edits.country || 'US'}
          onChange={handleChange}
          name="country"
          size="small"
          fullWidth
          disabled={selectedGateway === 'Telnyx'}
        >
          {options}
        </Select>
      </Stack>
    );
  };

  return (
    <React.Fragment>
      {generateLander()}
      <Box component="form" onChange={handleChange}>
        <Box sx={{ marginBottom: 4 }}>{generatePoolNumbersTable()}</Box>
        <Stack gap={2} maxWidth={800}>
          {generateDropdowns()}
          {generateCountryPicker()}
          {generateTextFormGroup('Prefix', 'prefix', true)}
          {generateNumberContainsFormGroup()}
          {generateErrorText()}
        </Stack>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginY: 4,
          }}
        >
          {generateSearchButton()}
        </Box>

        {generateAvailableTable()}
      </Box>
      {generateWarningModal()}
    </React.Fragment>
  );
};

export default ManagePoolNumber;
